import React, { useEffect, useState } from 'react';

import { SplitBlockImage, SplitContentBlockData } from 'api/cms';
import { Box } from 'components/Box';
import { bp, gutterWidth } from 'theme';
import { getImageSrc } from 'utils/getImageSrc';

import { BlockquoteContent } from './BlockquoteContent';
import { ImageContent } from './ImageContent';
import { TextContent } from './TextContent';
import { VideoContent } from './VideoContent';

const blockContentMap: { [key: string]: React.ElementType } = {
  IMAGE: ImageContent,
  TEXT: TextContent,
  VIDEO: VideoContent,
  BLOCKQUOTE: BlockquoteContent,
};

type Props = {
  blockId: string;
  data: SplitContentBlockData;
  blockIndex: number;
  pageUpdated: string;
  preview?: boolean;
};

export function SplitContentBlock({
  blockId,
  data: { sections, appearance },
  blockIndex,
  pageUpdated,
  preview,
}: Props) {
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const resizeHandler = () =>
      setIsDesktop(window && window.innerWidth >= bp[1]);

    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);
  const sectionsKey = `${sections[0].type}_${sections[1].type}`;
  const imageTextBlock = ['IMAGE_TEXT', 'TEXT_IMAGE'].includes(sectionsKey);
  const imageSection = sections.find(
    (section) => section.type === 'IMAGE'
  ) as SplitBlockImage;
  const showBackgroundImage =
    imageSection && imageSection.data.isMobileBackground && !isDesktop;

  const flipOnMobile =
    ['TEXT', 'BLOCKQUOTE'].includes(sections[0].type) &&
    ['IMAGE', 'VIDEO'].includes(sections[1].type);

  return (
    <Box
      data-qa-id={`split-content-block-${blockIndex}`}
      backgroundImage={
        showBackgroundImage
          ? `url('${getImageSrc(imageSection.data.image)}')`
          : undefined
      }
      backgroundPosition={showBackgroundImage ? 'center center' : undefined}
      role={showBackgroundImage ? 'img' : undefined}
      aria-label={
        showBackgroundImage ? imageSection.data.image.altText : undefined
      }
      mx={showBackgroundImage ? '-24px' : undefined}
    >
      <Box py={appearance !== 'SIDE_BY_SIDE' && gutterWidth}>
        <Box
          position="relative"
          display="flex"
          borderRadius={
            imageTextBlock && !showBackgroundImage
              ? ['12px', null, 0]
              : undefined
          }
          boxShadow={
            imageTextBlock && !showBackgroundImage
              ? ['0px 4px 9px 0px rgba(0, 0, 0, 0.1)', null, 'none']
              : undefined
          }
          flexDirection={[
            flipOnMobile ? 'column-reverse' : 'column',
            null,
            'row',
          ]}
          alignItems={appearance === 'FULL_WIDTH' && 'center'}
          m={[imageTextBlock ? 0 : -gutterWidth / 4, null, -gutterWidth / 2]}
          width={[
            imageTextBlock ? '100%' : `calc(100% + ${gutterWidth / 2}px)`,
            null,
            `calc(100% + ${gutterWidth}px)`,
          ]}
          minHeight={[
            'initial',
            null,
            appearance === 'FULL_WIDTH' && 'calc(100vh - 300px)',
          ]}
        >
          {sections.map((section, index) => {
            const Content = blockContentMap[section.type];
            return (
              <Content
                section={section}
                appearance={appearance}
                imageTextBlock={imageTextBlock}
                showBackgroundImage={showBackgroundImage}
                index={index}
                blockId={blockId}
                blockIndex={blockIndex}
                pageUpdated={pageUpdated}
                preview={preview}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
